import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/contexts/AuthContext'
import { ArrowLeft, BarChart3, ExternalLink, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import { functions } from '@/config/firebase'
import { httpsCallable } from 'firebase/functions'
import { DataSource } from '@/services/dataSourcesService'

interface GoogleAnalyticsSetupFormProps {
  onBack: () => void
  onComplete: (dataSource: any) => void
  organizationId: string
  editingDataSource?: DataSource | null
}

interface GoogleAnalyticsConfig {
  name: string
  propertyId: string
  refreshToken: string
}

// OAuth configuration - these should match your Firebase Functions environment variables
const GOOGLE_OAUTH_CONFIG = {
  clientId: import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID,
  redirectUri: `${window.location.origin}/auth/google/callback`,
  scope: 'https://www.googleapis.com/auth/analytics.readonly',
  responseType: 'code',
}

const GoogleAnalyticsSetupForm: React.FC<GoogleAnalyticsSetupFormProps> = ({
  onBack,
  onComplete,
  organizationId,
  editingDataSource
}) => {
  const [connectionName, setConnectionName] = useState('')
  const [propertyId, setPropertyId] = useState('')
  const [isConnecting, setIsConnecting] = useState(false)
  const [oauthStep, setOauthStep] = useState<'setup' | 'oauth' | 'complete'>('setup')
  const [authCode, setAuthCode] = useState('')
  const [refreshToken, setRefreshToken] = useState('')
  const { currentUser } = useAuth()
  const { toast } = useToast()

  // Populate form when editing
  useEffect(() => {
    if (editingDataSource && editingDataSource.type === 'google-analytics') {
      setConnectionName(editingDataSource.name)
      const config = editingDataSource.configuration as any
      if (config?.propertyId) {
        setPropertyId(config.propertyId)
      }
    }
  }, [editingDataSource])

  const isEditMode = !!editingDataSource

  const handleStartOAuth = () => {
    if (!connectionName.trim() || !propertyId.trim()) {
      toast({
        title: "Missing Information",
        description: "Please provide both connection name and Google Analytics Property ID.",
        variant: "destructive",
      })
      return
    }

    if (!GOOGLE_OAUTH_CONFIG.clientId) {
      toast({
        title: "Configuration Error",
        description: "Google OAuth client ID not configured. Please contact support.",
        variant: "destructive",
      })
      return
    }

    // Construct OAuth URL
    const oauthParams = new URLSearchParams({
      client_id: GOOGLE_OAUTH_CONFIG.clientId,
      redirect_uri: GOOGLE_OAUTH_CONFIG.redirectUri,
      scope: GOOGLE_OAUTH_CONFIG.scope,
      response_type: GOOGLE_OAUTH_CONFIG.responseType,
      access_type: 'offline', // Required to get refresh token
      prompt: 'consent', // Force consent screen to ensure refresh token
    })

    const oauthUrl = `https://accounts.google.com/o/oauth2/v2/auth?${oauthParams.toString()}`
    
    // Open OAuth in new window
    const popup = window.open(oauthUrl, 'google-oauth', 'width=500,height=600,scrollbars=yes,resizable=yes')
    
    setOauthStep('oauth')
    
    // Listen for the OAuth callback
    const checkClosed = setInterval(() => {
      if (popup?.closed) {
        clearInterval(checkClosed)
        // Check if we received the auth code via postMessage or URL
        setOauthStep('setup') // Reset if popup closed without completion
      }
    }, 1000)

    // Listen for postMessage from OAuth popup
    const messageHandler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      
      if (event.data.type === 'GOOGLE_OAUTH_SUCCESS') {
        clearInterval(checkClosed)
        popup?.close()
        setAuthCode(event.data.code)
        exchangeCodeForTokens(event.data.code)
        window.removeEventListener('message', messageHandler)
      } else if (event.data.type === 'GOOGLE_OAUTH_ERROR') {
        clearInterval(checkClosed)
        popup?.close()
        setOauthStep('setup')
        toast({
          title: "OAuth Failed",
          description: event.data.error || "Failed to authorize with Google.",
          variant: "destructive",
        })
        window.removeEventListener('message', messageHandler)
      }
    }

    window.addEventListener('message', messageHandler)
  }

  const exchangeCodeForTokens = async (code: string) => {
    try {
      setIsConnecting(true)
      
      // Exchange authorization code for tokens
      const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          code,
          client_id: GOOGLE_OAUTH_CONFIG.clientId!,
          client_secret: import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_SECRET!, // This should be handled securely
          redirect_uri: GOOGLE_OAUTH_CONFIG.redirectUri,
          grant_type: 'authorization_code',
        }),
      })

      if (!tokenResponse.ok) {
        throw new Error('Failed to exchange authorization code for tokens')
      }

      const tokenData = await tokenResponse.json()
      
      if (!tokenData.refresh_token) {
        throw new Error('No refresh token received. Please ensure you grant offline access.')
      }

      setRefreshToken(tokenData.refresh_token)
      setOauthStep('complete')
      
      // Now create the data source
      await createGoogleAnalyticsDataSource(tokenData.refresh_token)
      
    } catch (error: any) {
      console.error('Error exchanging OAuth code:', error)
      toast({
        title: "Token Exchange Failed",
        description: error.message || "Failed to complete OAuth flow.",
        variant: "destructive",
      })
      setOauthStep('setup')
    } finally {
      setIsConnecting(false)
    }
  }

  const updateGoogleAnalyticsDataSource = async () => {
    if (!editingDataSource) return;
    
    try {
      setIsConnecting(true)
      
      // For updates, we only update the name and propertyId - we don't need to re-authenticate
      const updateDataSource = httpsCallable(functions, 'updateDataSource')
      
      const response = await updateDataSource({
        dataSourceId: editingDataSource.id,
        name: connectionName,
        configuration: {
          ...editingDataSource.configuration,
          propertyId: propertyId,
        },
        description: `Google Analytics 4 property ${propertyId}`,
      })

      const result = response.data as any

      if (result.success) {
        toast({
          title: "Google Analytics Updated",
          description: `Successfully updated connection "${connectionName}".`,
          variant: "success",
        })
        
        onComplete({
          id: editingDataSource.id,
          name: connectionName,
          type: 'google-analytics',
          propertyId: propertyId,
        })
      } else {
        throw new Error(result.message || 'Failed to update Google Analytics data source')
      }
      
    } catch (error: any) {
      console.error('Error updating Google Analytics data source:', error)
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update Google Analytics data source.",
        variant: "destructive",
      })
    } finally {
      setIsConnecting(false)
    }
  }

  const createGoogleAnalyticsDataSource = async (refreshTokenValue: string) => {
    try {
      setIsConnecting(true)
      
      const createDataSource = httpsCallable(functions, 'createDataSource')
      
      const response = await createDataSource({
        name: connectionName,
        type: 'google-analytics',
        organizationId: organizationId,
        configuration: {
          authType: 'oauth2',
          propertyId: propertyId,
          refreshToken: refreshTokenValue,
        },
        description: `Google Analytics 4 property ${propertyId}`,
        visibility: 'organization',
      })

      const result = response.data as any

      if (result.success) {
        toast({
          title: "Google Analytics Connected",
          description: `Successfully connected to GA4 property ${propertyId}.`,
          variant: "success",
        })
        
        onComplete({
          id: result.dataSourceId,
          name: connectionName,
          type: 'google-analytics',
          propertyId: propertyId,
        })
      } else {
        throw new Error(result.message || 'Failed to create Google Analytics data source')
      }
      
    } catch (error: any) {
      console.error('Error creating Google Analytics data source:', error)
      toast({
        title: "Connection Failed",
        description: error.message || "Failed to create Google Analytics data source.",
        variant: "destructive",
      })
      setOauthStep('setup')
    } finally {
      setIsConnecting(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Data Sources
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
          <BarChart3 className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold leading-tight text-foreground">
            {isEditMode ? `Edit ${editingDataSource?.name}` : 'Connect Google Analytics 4'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEditMode 
              ? 'Update your GA4 property configuration' 
              : 'Connect your GA4 property to analyze website traffic and user behavior'
            }
          </p>
        </div>
      </div>

      {/* Setup Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Google Analytics 4 Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {oauthStep === 'setup' && (
            <>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="connectionName">Connection Name</Label>
                  <Input
                    id="connectionName"
                    value={connectionName}
                    onChange={(e) => setConnectionName(e.target.value)}
                    placeholder="e.g., My Website Analytics"
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    A friendly name to identify this connection
                  </p>
                </div>

                <div>
                  <Label htmlFor="propertyId">GA4 Property ID</Label>
                  <Input
                    id="propertyId"
                    value={propertyId}
                    onChange={(e) => setPropertyId(e.target.value)}
                    placeholder="123456789"
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Find this in GA4: Admin → Property Settings → Property details
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900">Before You Connect</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Make sure you have admin access to the Google Analytics property you want to connect.
                      You'll be redirected to Google to authorize FireForge to access your analytics data.
                    </p>
                  </div>
                </div>
              </div>

              <Button
                onClick={isEditMode ? updateGoogleAnalyticsDataSource : handleStartOAuth}
                disabled={!connectionName.trim() || !propertyId.trim() || isConnecting}
                className="w-full"
              >
                {isConnecting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {isEditMode ? 'Updating...' : 'Connecting...'}
                  </>
                ) : (
                  <>
                    {isEditMode ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Update Connection
                      </>
                    ) : (
                      <>
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Connect with Google
                      </>
                    )}
                  </>
                )}
              </Button>
            </>
          )}

          {oauthStep === 'oauth' && (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Waiting for Authorization</h3>
              <p className="text-muted-foreground">
                Please complete the authorization in the popup window to continue.
              </p>
            </div>
          )}

          {oauthStep === 'complete' && isConnecting && (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Creating Connection</h3>
              <p className="text-muted-foreground">
                Setting up your Google Analytics data source...
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Need Help?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm space-y-2">
            <p><strong>Finding your GA4 Property ID:</strong></p>
            <ol className="list-decimal list-inside space-y-1 text-muted-foreground ml-2">
              <li>Go to Google Analytics</li>
              <li>Click "Admin" in the bottom left</li>
              <li>In the Property column, click "Property Settings"</li>
              <li>Your Property ID is shown at the top</li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default GoogleAnalyticsSetupForm 